import { AnimatePresence, motion } from "framer-motion";

import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

type PlayerUIContextType = {
  showCenterContent: (content: React.ReactNode) => void;
};

const PlayerUIContext = createContext<PlayerUIContextType | null>(null);

export const usePlayerUIContext = () => {
  const context = useContext(PlayerUIContext);
  if (!context) {
    throw new Error("usePlayerUIContext must be used inside PlayerUIProvider");
  }
  return context;
};

export const PlayerUIProvider = ({ children, loading }: { children: React.ReactNode; loading: boolean }) => {
  const [content, setContent] = useState<React.ReactNode>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const showCenterContent = (node: React.ReactNode) => {
    setContent(node);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setContent(null);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const contextValue = useMemo(() => ({ showCenterContent }), []);

  return (
    <PlayerUIContext.Provider value={contextValue}>
      {children}
      <CenterContentOverlay content={content} loading={loading} />
    </PlayerUIContext.Provider>
  );
};

const CenterContentOverlay = ({ content, loading }: { content: React.ReactNode; loading: boolean }) => {
  return (
    <AnimatePresence>
      {!loading && content && (
        <motion.div
          key="center-content"
          className="pointer-events-none absolute left-[calc(1/2-60px)] top-[calc(1/2-60px)] z-[10000000] flex h-[120px] w-[120px] -translate-x-1/2 -translate-y-1/2 items-center justify-center overflow-hidden rounded-full bg-[#ffffff14] text-white max-lg:left-[calc(1/2-40px)] max-lg:top-[calc(1/2-40px)] max-lg:h-20 max-lg:w-20"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
        >
          {content}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
